import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Card,
  Spinner,
  StatusBadge,
  Input,
} from '../../components/design-system';
import { AdminPage } from './AdminPage';

interface LegalDoc {
  id: string;
  slug: string;
  title: string;
  audience: string;
  content_md: string;
  published: boolean;
  updated_at: string;
  created_at: string;
}

interface DocForm {
  slug: string;
  title: string;
  audience: string;
  content_md: string;
  published: boolean;
}

const EMPTY_FORM: DocForm = { slug: '', title: '', audience: 'all', content_md: '', published: true };
const AUDIENCES = ['all', 'traveler', 'host', 'admin'];
const AUDIENCE_LABELS: Record<string, string> = { all: 'Everyone', traveler: 'Traveler', host: 'Host', admin: 'Admin' };
const AUDIENCE_COLORS: Record<string, string> = {
  all: 'bg-app-fg/10 text-app-fg',
  traveler: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  host: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  admin: 'bg-violet/10 text-violet border-violet/20',
};

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function AdminLegal() {
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<(DocForm & { id?: string }) | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);

  async function load() {
    const { data } = await supabase.from('legal_documents').select('*').order('created_at');
    setDocs((data ?? []) as LegalDoc[]);
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  function openEditor(doc?: LegalDoc) {
    if (doc) {
      setEditing({ id: doc.id, slug: doc.slug, title: doc.title, audience: doc.audience, content_md: doc.content_md, published: doc.published });
    } else {
      setEditing({ ...EMPTY_FORM });
    }
    setPreviewMode(false);
    setSaveState('idle');
  }

  async function handleSave() {
    if (!editing) return;
    setSaveState('saving');
    const payload = {
      slug: editing.slug,
      title: editing.title,
      audience: editing.audience,
      content_md: editing.content_md,
      published: editing.published,
      updated_at: new Date().toISOString(),
      updated_by: (await supabase.auth.getUser()).data.user?.id ?? null,
    };
    if (editing.id) {
      await supabase.from('legal_documents').update(payload).eq('id', editing.id);
    } else {
      await supabase.from('legal_documents').insert(payload);
    }
    setSaveState('saved');
    setTimeout(() => { setSaveState('idle'); setEditing(null); }, 1200);
    await load();
  }

  async function handleDelete(id: string) {
    await supabase.from('legal_documents').delete().eq('id', id);
    setDeleting(null);
    await load();
  }

  function exportMarkdown(doc: LegalDoc) {
    const header = `# ${doc.title}\n\n`;
    const blob = new Blob([header + doc.content_md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.slug}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const inputClass = 'rounded-md border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none';

  if (loading) return <AdminPage title="Legal"><div className="flex justify-center py-16"><Spinner /></div></AdminPage>;

  // ─── Editor ──────────────────────────────────────────────────────
  if (editing) {
    return (
      <AdminPage
        title={editing.id ? `Edit: ${editing.title || 'Document'}` : 'New Legal Document'}
        actions={
          <div className="flex items-center gap-3">
            <button onClick={() => setEditing(null)} className="text-sm text-app-muted hover:text-app-fg">Cancel</button>
            <Button onClick={() => void handleSave()} disabled={saveState === 'saving' || !editing.title.trim() || !editing.slug.trim()}>
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : editing.id ? 'Save changes' : 'Create'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-6">
          {/* Meta */}
          <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
            <h4 className="text-sm font-semibold text-app-fg">Document info</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input label="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Terms of Service" />
              <Input
                label="Slug"
                value={editing.slug}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                placeholder="terms-of-service"
              />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-app-fg-2">Audience</label>
                <select value={editing.audience} onChange={(e) => setEditing({ ...editing, audience: e.target.value })}
                  className={`w-full appearance-none ${inputClass} pr-9`}>
                  {AUDIENCES.map((a) => <option key={a} value={a}>{AUDIENCE_LABELS[a]}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                className="rounded border-app-border bg-app-bg text-accent" />
              <span className="text-app-fg-2">Published</span>
            </label>
          </div>

          {/* Content editor with preview toggle */}
          <div className="bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-app-fg">Content (Markdown)</h4>
              <div className="flex rounded-lg border border-app-border overflow-hidden">
                <button
                  onClick={() => setPreviewMode(false)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${!previewMode ? 'bg-app-fg/10 text-app-fg' : 'text-app-muted hover:text-app-fg'}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => setPreviewMode(true)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${previewMode ? 'bg-app-fg/10 text-app-fg' : 'text-app-muted hover:text-app-fg'}`}
                >
                  Preview
                </button>
              </div>
            </div>
            {previewMode ? (
              <div className="prose-legal min-h-[300px] rounded-xl border border-app-border bg-app-bg p-5 overflow-y-auto max-h-[70vh]">
                <ReactMarkdown>{editing.content_md}</ReactMarkdown>
              </div>
            ) : (
              <textarea
                value={editing.content_md}
                onChange={(e) => setEditing({ ...editing, content_md: e.target.value })}
                rows={20}
                placeholder="Write your legal document in Markdown…"
                className={`${inputClass} resize-y min-h-[300px] font-mono text-xs leading-relaxed`}
              />
            )}
          </div>
        </div>
      </AdminPage>
    );
  }

  // ─── List ────────────────────────────────────────────────────────
  return (
    <AdminPage
      title="Legal"
      description={`${docs.length} document${docs.length !== 1 ? 's' : ''}`}
      actions={<Button onClick={() => openEditor()}>+ New document</Button>}
    >
      {docs.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-sm text-app-muted">No legal documents yet.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {docs.map((doc) => (
            <Card key={doc.id} className="p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-app-fg">{doc.title}</h3>
                    <span className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 border ${AUDIENCE_COLORS[doc.audience] ?? AUDIENCE_COLORS.all}`}>
                      {AUDIENCE_LABELS[doc.audience] ?? doc.audience}
                    </span>
                    <StatusBadge tone={doc.published ? 'success' : 'neutral'} label={doc.published ? 'Published' : 'Draft'} />
                  </div>
                  <p className="text-xs text-app-muted">
                    /{doc.slug} · Updated {new Date(doc.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => exportMarkdown(doc)} className="text-xs text-violet/70 hover:text-violet transition-colors">Export .md</button>
                  <button onClick={() => openEditor(doc)} className="text-xs text-app-muted hover:text-app-fg transition-colors">Edit</button>
                  <button onClick={() => setDeleting(doc.id)} className="text-xs text-crimson/60 hover:text-crimson transition-colors">Delete</button>
                </div>
              </div>
              <div className="prose-legal text-xs text-app-muted line-clamp-3">
                <ReactMarkdown>{doc.content_md.slice(0, 300)}</ReactMarkdown>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {deleting && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => setDeleting(null)}>
          <div className="bg-app-card border border-app-border rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-app-fg mb-2">Delete document?</h3>
            <p className="text-sm text-app-muted mb-6">This cannot be undone.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleting(null)} className="text-sm text-app-muted hover:text-app-fg px-4 py-2">Cancel</button>
              <Button onClick={() => void handleDelete(deleting)} className="bg-crimson hover:bg-crimson/90">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}

export default AdminLegal;
